import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssistantCalendar,
  AssistantConnection,
  AssistantEvent,
  AssistantOAuthProviderKey,
  AssistantStatus,
  AssistantSummary,
  AssistantSyncRun,
  CarriesConnectionInput,
  UpdateCalendarInput,
  UpdateConnectionInput,
} from '@accounting/shared';
import { api } from '../lib/api';

/**
 * Personal-assistant data access. Nothing here is company-scoped: keys live
 * under ['me', 'assistant', ...] like the Plaid items page, and the active
 * company is ignored.
 */
export const assistantKeys = {
  all: ['me', 'assistant'] as const,
  status: () => ['me', 'assistant', 'status'] as const,
  summary: () => ['me', 'assistant', 'summary'] as const,
  connections: () => ['me', 'assistant', 'connections'] as const,
  syncRuns: (id: string) => ['me', 'assistant', 'sync-runs', id] as const,
  calendars: () => ['me', 'assistant', 'calendars'] as const,
  events: (from: string, to: string, connectionIds: string[]) =>
    ['me', 'assistant', 'events', from, to, [...connectionIds].sort().join(',')] as const,
};

export function useAssistantStatus() {
  return useQuery({
    queryKey: assistantKeys.status(),
    queryFn: () => api.get<AssistantStatus>('/assistant/status'),
    staleTime: 5 * 60_000,
  });
}

/** Sidebar badge feed. Polled no faster than once a minute (global limiter is 200 req/min). */
export function useAssistantSummary(enabled = true) {
  return useQuery({
    queryKey: assistantKeys.summary(),
    queryFn: () => api.get<AssistantSummary>('/assistant/summary'),
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useConnections() {
  return useQuery({
    queryKey: assistantKeys.connections(),
    queryFn: () => api.get<{ connections: AssistantConnection[] }>('/assistant/connections').then((r) => r.connections),
    staleTime: 30_000,
  });
}

export function useSyncRuns(connectionId: string | null) {
  return useQuery({
    queryKey: assistantKeys.syncRuns(connectionId ?? ''),
    queryFn: () =>
      api
        .get<{ runs: AssistantSyncRun[] }>(`/assistant/connections/${connectionId}/sync-runs`, { limit: '8' })
        .then((r) => r.runs),
    enabled: !!connectionId,
    staleTime: 15_000,
  });
}

export function useCalendars() {
  return useQuery({
    queryKey: assistantKeys.calendars(),
    queryFn: () => api.get<{ calendars: AssistantCalendar[] }>('/assistant/calendars').then((r) => r.calendars),
    staleTime: 60_000,
  });
}

export function useEvents(from: string, to: string, connectionIds: string[]) {
  return useQuery({
    queryKey: assistantKeys.events(from, to, connectionIds),
    queryFn: () =>
      api
        .get<{ events: AssistantEvent[] }>('/assistant/events', {
          from,
          to,
          ...(connectionIds.length > 0 ? { connectionIds: connectionIds.join(',') } : {}),
        })
        .then((r) => r.events),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: assistantKeys.all });
}

// ── OAuth popup flow ───────────────────────────────────────────────────────

export interface OAuthPopupResult {
  code: string;
  state: string;
}

const OAUTH_MESSAGE_TYPE = 'assistant-oauth';
const OAUTH_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Resolve when the callback page posts the code back, reject when the popup is
 * closed first or the provider returned an error. The popup must be opened by
 * the click handler itself (synchronously) or the browser blocks it — which is
 * why the caller opens `about:blank` first and we navigate it afterwards.
 */
export function waitForOAuthPopup(popup: Window): Promise<OAuthPopupResult> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closedPoll);
      clearTimeout(timeout);
      fn();
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; code?: string; state?: string; error?: string; errorDescription?: string } | null;
      if (!d || d.type !== OAUTH_MESSAGE_TYPE) return;
      if (d.error) {
        finish(() => reject(new Error(d.errorDescription || d.error || 'Sign-in was refused')));
        return;
      }
      if (d.code && d.state) finish(() => resolve({ code: d.code!, state: d.state! }));
    };
    const closedPoll = setInterval(() => {
      if (popup.closed) finish(() => reject(new Error('The sign-in window was closed before finishing.')));
    }, 500);
    const timeout = setTimeout(() => finish(() => reject(new Error('Sign-in timed out.'))), OAUTH_TIMEOUT_MS);
    window.addEventListener('message', onMessage);
  });
}

export function useConnectProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { provider: AssistantOAuthProviderKey; label?: string; reconnectConnectionId?: string }) => {
      const popup = window.open('about:blank', 'assistant-oauth', 'popup=yes,width=600,height=760');
      if (!popup) throw new Error('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.');
      try {
        const { authorizeUrl } = await api.post<{ authorizeUrl: string }>('/assistant/connections/oauth/start', input);
        popup.location.href = authorizeUrl;
        const { code, state } = await waitForOAuthPopup(popup);
        return api.post<{ connection: AssistantConnection; isNew: boolean }>('/assistant/connections/oauth/callback', { code, state });
      } catch (err) {
        if (!popup.closed) popup.close();
        throw err;
      }
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateConnectionInput & { id: string }) =>
      api.patch<{ connection: AssistantConnection }>(`/assistant/connections/${id}`, input).then((r) => r.connection),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assistant/connections/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSyncNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ run: AssistantSyncRun }>(`/assistant/connections/${id}/sync`, {}).then((r) => r.run),
    onSettled: () => invalidateAll(qc),
  });
}

export function useUpdateCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateCalendarInput & { id: string }) =>
      api.patch<{ calendar: AssistantCalendar }>(`/assistant/calendars/${id}`, input).then((r) => r.calendar),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assistantKeys.calendars() });
      void qc.invalidateQueries({ queryKey: ['me', 'assistant', 'events'] });
    },
  });
}

export const PROVIDER_LABELS: Record<string, string> = {
  microsoft: 'Microsoft 365',
  google: 'Google',
  imap: 'IMAP mailbox',
  carries: "Carrie's Bar",
};

export function useConnectCarries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CarriesConnectionInput) =>
      api.post<{ connection: AssistantConnection; isNew: boolean }>('/assistant/connections/carries', input),
    onSuccess: () => invalidateAll(qc),
  });
}
